import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { IconButton, Box, Chip, Stack, ToggleButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
  listProjectOrders,
  setProjectOrdersFilters,
  resetProjectOrdersFilters,
} from '../../modules/projectModule/project-orders';
import ProjectOrdersList from '../../components/project_orders/ProjectOrdersList';
import { getNumberComma } from '../../utils/numberUtils';
import { ProjectOrdersListBox } from '../../components/project_orders/ProjectOrdersListBox';
import OrderViewModal from '../../components/project_orders/OrderViewModal';
import OrderBillReviewDialog from './OrderBillReviewContainer';
import { FilterToggleRow } from '../../components/common/list/FilterToggleGroup';

// 미발행/미수금/지연/삭제이력 칩: 서로 독립된 불린 필터(AND 결합)라 FilterToggleGroup
// (단일선택 exclusive)이 아니라 개별 ToggleButton(selected/onChange 직접 제어)을 쓴다.
// 2026-10-22 chancemng 토글 스타일(size small, textTransform none)만 맞춤.
const toggleSx = { textTransform: 'none', px: 1.1, py: 0.4, lineHeight: 1.4, whiteSpace: 'nowrap' };

const ProjectOrdersListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { orderLetters, defaultFilters, error, loading } = useSelector(
    ({ projectModule, loading }) => ({
      orderLetters: projectModule.projectOrders.orderLetters,
      defaultFilters: projectModule.projectOrders.defaultFilters,
      error: projectModule.projectOrders.error,
      loading: loading['project/LIST_PROJECT_ORDERS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
      ordYn: filters.ordYn || 'Y',
      // 계산서 현황 필터(사용자 요구 2026-10-21, Part B) - 각각 AND로 겹쳐 적용된다.
      filterUnissued: filters.filterUnissued ? 'Y' : undefined,
      filterUnpaid: filters.filterUnpaid ? 'Y' : undefined,
      filterOverdue: filters.filterOverdue ? 'Y' : undefined,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listProjectOrders(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (orderLetters) {
      setTableData(orderLetters);
    }
  }, [orderLetters]);

  const handleFilters = useCallback((name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };

    dispatch(setProjectOrdersFilters(newFilters));
  }, []);

  // 삭제 이력 보기(BITO_0425 p14/15, 0515 p25/26 "■사용 □미사용", "삭제 이력도 추가 표시").
  // 품의서는 하드 삭제 대신 ORD_YN='N'으로 남기므로, 토글을 켜면 그 미사용(삭제) 건만 보여준다.
  const showingDeleted = defaultFilters.ordYn === 'N';
  const handleToggleDeleted = useCallback(() => {
    dispatch(
      setProjectOrdersFilters({
        ...defaultFilters,
        ordYn: showingDeleted ? 'Y' : 'N',
      }),
    );
  }, [defaultFilters, showingDeleted, dispatch]);

  // 계산서 현황 필터(사용자 요구 2026-10-21, Part B) - 체크박스 3개를 동시에 켜면 전부
  // AND로 겹쳐 적용된다(OrdersController.getOrderList).
  const handleToggleBillFilter = useCallback(
    (key) => {
      dispatch(
        setProjectOrdersFilters({
          ...defaultFilters,
          [key]: !defaultFilters[key],
        }),
      );
    },
    [defaultFilters, dispatch],
  );

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
      },
      {
        field: 'roUuid',
        hide: true,
        type: 'string',
        editable: false,
      },
      {
        field: 'roNo',
        headerName: '수주품의서번호',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
      },
      {
        // 수주품의서 관리 per-order 하위 항목(사용자 요구 2026-10-21, 규칙 3) -
        // 매출/매입 세금계산서 회차·상태·합계를 보여주고 재배치/취소를 바로 할 수 있게 한다.
        field: 'billReview',
        headerName: '계산서 현황',
        minWidth: 110,
        flex: 0.5,
        sortable: false,
        filterable: false,
        type: 'string',
        editable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="계산서 현황"
            color="primary"
            size="small"
            onClick={() => handleOpenReview(params.row)}
          >
            <ReceiptLongIcon />
          </IconButton>
        ),
      },
      // {
      //   field: 'expDay',
      //   headerName: '계약일',
      //   minWidth: 80,
      //   flex: 0.5,
      //   type: 'string',
      //   editable: false,
      // },
      // {
      //   field: 'siDt',
      //   headerName: '계약 종료일',
      //   minWidth: 80,
      //   flex: 0.5,
      //   type: 'string',
      //   editable: false,
      // },

       {
        field: 'crtDptNm',
        headerName: '부서명',
        minWidth: 80,
        flex: 0.5,
        type: 'string',
        editable: false,
      },

      {
        field: 'crtNm',
        headerName: '담당자',
        minWidth: 60,
        flex: 0.5,
        type: 'string',
        editable: false,
      },
      {
        field: 'partnerNm',
        headerName: '매출처명',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 300,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
        renderCell: (params) => (
          <>
            {params.value}
            <IconButton
              aria-label="preview"
              color="success"
              size="small"
              onClick={() => handleOpen(params.row)}
            >
              <PreviewIcon />
            </IconButton>
          </>
        ),
      },

      // {
      //   field: 'crtNm',
      //   headerName: '등록자',
      //   minWidth: 80,
      //   flex: 1,
      //   type: 'string',
      //   editable: false,
      // },

      {
        field: 'roTotalAmt',
        headerName: '매출액',
        minWidth: 80,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        valueFormatter: (value) => {
          //console.log(value);
          if (value === undefined || value === null) return '0';
          return getNumberComma(value || 0);
        },
        renderCell: (params) =>
          {
            //console.log(params);
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },

      },
      {
        field: 'expBuy',
        headerName: '매입액',
        minWidth: 80,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        valueFormatter: (value) => {
          if (value === undefined || value === null) return '0';
          return getNumberComma(value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      {
        field: 'roProfitAmt',
        headerName: '매출이익',
        minWidth: 80,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        valueFormatter: (value) => {
          if (value === undefined || value === null) return '0';
          return getNumberComma(value || 0);
        },
        renderCell: (params) => {
          if (!params || params.value === undefined) return '0';
          return getNumberComma(params.value || 0);
        },
      },
      
      {
        // 계산서 현황 총계(사용자 요구 2026-10-21, Part B) -
        // OrderBillCancelService.summarizeOrderBillStatus()가 채운다.
        field: 'billIssuedAmt',
        headerName: '발행액',
        minWidth: 110,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{getNumberComma(params.value || 0)}</span>
            {params.row.billOverdueIssue && (
              <Chip size="small" color="error" label="발행 지연" />
            )}
          </Stack>
        ),
      },
      {
        field: 'billCollectedAmt',
        headerName: '수금액',
        minWidth: 110,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        renderCell: (params) => getNumberComma(params.value || 0),
      },
      {
        field: 'billOutstandingAmt',
        headerName: '미수금',
        minWidth: 110,
        flex: 1,
        type: 'number',
        align: 'right',
        editable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>{getNumberComma(params.value || 0)}</span>
            {params.row.billOverdueCollect && (
              <Chip size="small" color="error" label="수금 지연" />
            )}
          </Stack>
        ),
      },
      {
        field: 'ordYn',
        headerName: '사용여부',
        minWidth: 30,
        flex: 1,
        type: 'string',
        editable: false,
        renderCell: (params) =>
          params.value === 'N' ? '미사용' : '사용',
      },
      {
        field: 'crtDt',
        headerName: '등록일',
        minWidth: 100,
        flex: 1,
        type: 'date',
        valueGetter: (value) => {
          if (!value) return null;
          try {
            return new Date(value);
          } catch (e) {
            console.error('Invalid date format:', value);
            return null;
          }
        },
        valueFormatter: (value) => {
          if (!value) return '';
          return dayjs(value).format('YYYY-MM-DD');
        },
        editable: false,
      },
    ],
    [],
  );

  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        let searchData = {
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
          // 검색해도 "삭제 이력 보기" 토글 상태는 유지한다.
          ordYn: defaultFilters.ordYn,
        };
        dispatch(setProjectOrdersFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  const [showOrder, setShowOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleClose = () => {
    setShowOrder(false);
    setSelectedOrder(null);
  };

  const handleOpen = (param) => {
    setSelectedOrder(param);
    setShowOrder(true);
  };

  // 계산서 현황 하위 패널(사용자 요구 2026-10-21, 규칙 3 - 수주품의서 관리에서 품의서별
  // 매출/매입 계산서 회차 모니터링 + 재배치/취소).
  const [reviewRoUuid, setReviewRoUuid] = useState(null);
  const handleOpenReview = (row) => setReviewRoUuid(row.roUuid);
  const handleCloseReview = () => setReviewRoUuid(null);
  const handleReviewChanged = () => dispatch(listProjectOrders(makeFilters(defaultFilters)));

  // 검색 카드 "초기화" - 기존 reset 액션(RESET_PROJECT_ORDERS_FILTERS) 재사용.
  const handleReset = useCallback(() => {
    dispatch(resetProjectOrdersFilters());
  }, [dispatch]);

  return (
    <>
      <BreadcrumbsContainer
        title="수주 관리/수주품의서 현황/수주품의서 리스트"
        icon="order"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <ProjectOrdersListBox formik={formik} onReset={handleReset} />
        <FilterToggleRow>
          <ToggleButton
            size="small"
            value="filterUnissued"
            selected={Boolean(defaultFilters.filterUnissued)}
            onChange={() => handleToggleBillFilter('filterUnissued')}
            color="info"
            sx={toggleSx}
          >
            미발행 있음
          </ToggleButton>
          <ToggleButton
            size="small"
            value="filterUnpaid"
            selected={Boolean(defaultFilters.filterUnpaid)}
            onChange={() => handleToggleBillFilter('filterUnpaid')}
            color="info"
            sx={toggleSx}
          >
            미수금 있음
          </ToggleButton>
          <ToggleButton
            size="small"
            value="filterOverdue"
            selected={Boolean(defaultFilters.filterOverdue)}
            onChange={() => handleToggleBillFilter('filterOverdue')}
            color="error"
            sx={toggleSx}
          >
            지연 있음
          </ToggleButton>
          <ToggleButton
            size="small"
            value="showingDeleted"
            selected={showingDeleted}
            onChange={handleToggleDeleted}
            color="error"
            sx={toggleSx}
          >
            <HistoryIcon fontSize="small" sx={{ mr: 0.5 }} />
            {showingDeleted ? '삭제 이력 보기 (해제)' : '삭제 이력 보기'}
          </ToggleButton>
        </FilterToggleRow>
      </Box>
      <ProjectOrdersList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
        columnVisibilityModel={{
          id: false,
          roUuid: false
        }} 
      />
      {showOrder && (
        <OrderViewModal
          id={selectedOrder?.roUuid}
          open={showOrder}
          handleClose={handleClose}
          hiddenFields={{
            id: true,
            roUuid: true
          }}
        />
      )}
      {reviewRoUuid && (
        <OrderBillReviewDialog
          open={Boolean(reviewRoUuid)}
          onClose={handleCloseReview}
          roUuid={reviewRoUuid}
          mode="manage"
          onChanged={handleReviewChanged}
        />
      )}
    </>
  );
};

export default ProjectOrdersListContainer;
